import React, { useState } from 'react';
import { Product } from '../types';
import { getWindowOpenAt, getWindowState, formatWindowClosedMonth } from '../utils/productWindows';

interface ProductCardProps {
  product: Product;
  onClick?: (product: Product) => void;
}

// Archive register: grayscale imagery colorizing on hover, monospace
// catalogue lines, no badges, no quick view, no urgency styling.
const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Upcoming = window not yet open (windowOpenAt, with dropDate as fallback)
  const windowOpenAt = getWindowOpenAt(product);
  const windowState = getWindowState(product);
  const showComingSoon = windowState === 'upcoming'
    || (product.status === 'coming_soon' && (!windowOpenAt || new Date(windowOpenAt) > new Date()));
  // Closed fragments appear in the record, marked clearly — held, not sold
  const isClosed = product.tier === 'fragment' && windowState === 'closed';

  // Day-granularity only — a stated fact, not a ticking clock
  const opensLine = (() => {
    if (!showComingSoon || !windowOpenAt) return 'Surfacing soon';
    const days = Math.ceil((+new Date(windowOpenAt) - Date.now()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Opens today';
    if (days === 1) return 'Opens in 1 day';
    return `Opens in ${days} days`;
  })();

  // Touch devices have no hover, so the grayscale→color reveal never fires
  // there. Claimable cards render in full color on coarse pointers (phones,
  // tablets); closed and upcoming cards stay grayscale everywhere — the
  // state is the message. Desktop keeps the hover ritual.
  const atRest = (isClosed || showComingSoon)
    ? 'grayscale opacity-90'
    : 'grayscale opacity-90 pointer-coarse:grayscale-0 pointer-coarse:opacity-100';
  const imageClasses = `absolute inset-0 w-full h-full object-cover object-center filter transition-all duration-500 ${
    isHovered ? 'grayscale-0 opacity-100' : atRest
  }`;

  return (
    <div
      className="group relative flex flex-col cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick && onClick(product)}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
        <img
          src={product.image || '/assets/placeholder.svg'}
          alt={product.title}
          onError={(e) => {
            const t = e.currentTarget;
            if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
          }}
          className={`${imageClasses} ${isHovered && product.hoverImage && product.hoverImage !== product.image ? 'opacity-0' : ''}`}
        />
        {product.hoverImage && product.hoverImage !== product.image && (
          <img
            src={product.hoverImage}
            alt={`${product.title} Hover`}
            onError={(e) => {
              const t = e.currentTarget;
              if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
            }}
            className={`${imageClasses} ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          />
        )}

        {showComingSoon && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white p-4 text-center">
             <span className="font-mono text-xs tracking-[0.2em] uppercase">
                {opensLine}
             </span>
          </div>
        )}

        {isClosed && (
          <div className="absolute top-4 left-4 bg-black text-white font-mono text-[10px] tracking-[0.2em] px-2 py-1 z-20 uppercase">
            Closed
          </div>
        )}
      </div>

      <div className={`text-center space-y-1 ${isClosed ? 'opacity-60 group-hover:opacity-100 transition-opacity' : ''}`}>
        <h3 className="font-heading font-bold text-lg tracking-wide uppercase group-hover:text-brand-gray transition-colors">
          {product.title}
        </h3>
        {isClosed ? (
          <p className="font-mono text-xs text-gray-500 tracking-widest">
            {product.fragmentNumber ? `Fragment ${product.fragmentNumber}. ` : ''}
            {product.windowCloseAt ? `Window closed ${formatWindowClosedMonth(product.windowCloseAt)}.` : 'Window closed.'}
          </p>
        ) : (
          <>
            <p className="font-mono text-xs text-gray-500 tracking-widest">{product.category}</p>
            <p className="font-heading font-medium tracking-wide">
              £{product.price.toFixed(2)}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
